import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { baoMatRepository } from '../../../lib/repositories/bao-mat.repository';
import { useToast } from '../../../lib/toast';
import { capNhatCanhBao, gopTrangCanhBao } from '../nghiep-vu/canh-bao-audit';
import type { BoLocCanhBao, CanhBaoAudit, TrangCanhBao } from '../types';

const khoaCanhBao = (tenantId: string) => ['bao-mat', 'canh-bao', tenantId] as const;

type DuLieu = InfiniteData<TrangCanhBao, string | null>;

/** 4xx là câu trả lời của server (thiếu quyền, sai tenant): thử lại chỉ bắt chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/**
 * Cảnh báo audit đã kích hoạt của một tenant (ADR-0059 mục 4.1), lọc ở server
 * theo mức và trạng thái, phân trang con trỏ bằng "Tải thêm".
 */
export function useCanhBaoAudit(tenantId: string, boLoc: BoLocCanhBao) {
  const query = useInfiniteQuery<
    TrangCanhBao,
    ApiError,
    DuLieu,
    readonly [...ReturnType<typeof khoaCanhBao>, BoLocCanhBao],
    string | null
  >({
    queryKey: [...khoaCanhBao(tenantId), boLoc],
    queryFn: ({ pageParam }) => baoMatRepository.canhBao(tenantId, boLoc, pageParam),
    initialPageParam: null,
    getNextPageParam: (trang) => trang.next_cursor,
    staleTime: 30_000,
    retry: thuLai,
  });
  const dong = query.data ? gopTrangCanhBao(query.data.pages) : [];
  return { ...query, dong };
}

/**
 * Đánh dấu đã xem. Thành công thì thay dòng tại chỗ ở **mọi** bộ lọc đã tải,
 * không tải lại từ đầu (mất phần đã "Tải thêm"). Dòng vẫn nằm trong bộ lọc
 * "Chưa xem" tới lần tải sau — biến mất ngay dưới tay người bấm thì khó theo.
 */
export function useXacNhanCanhBao(tenantId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<CanhBaoAudit, ApiError, CanhBaoAudit>({
    mutationFn: (c) => baoMatRepository.xacNhanCanhBao(tenantId, c.id),
    onSuccess: (moi) => {
      qc.setQueriesData<DuLieu>({ queryKey: khoaCanhBao(tenantId) }, (cu) =>
        cu ? capNhatCanhBao(cu, moi) : cu,
      );
      toast.success(t('canhBaoAudit.daXacNhan', { ten: moi.rule_name }));
    },
  });
}
